/**
 * Push subscription endpoint URL validation.
 *
 * ## Why an allow-list?
 *
 * A compromised or malicious backend could attempt to supply an attacker-
 * controlled push endpoint when registering a subscription record for a
 * victim device. If the client accepted any HTTPS URL, the server could
 * redirect Web Push traffic to an endpoint it controls — effectively
 * exfiltrating metadata (message timing, presence) even without breaking
 * the E2EE payload encryption. The allow-list of known browser push
 * services prevents this: the client will only register endpoints whose
 * hostname it recognises as a legitimate push gateway operated by a
 * browser vendor.
 *
 * ## Current allow-list
 *
 * | Host | Browser / platform |
 * |---|---|
 * | `fcm.googleapis.com` | Chrome, Chromium-based browsers (Blink engine), Android WebView |
 * | `updates.push.services.mozilla.com` | Firefox (Gecko engine) |
 * | `web.push.apple.com` | Safari 16+ (macOS/iOS), WebKit-based browsers |
 * | `push.services.mozilla.com` | Firefox legacy endpoint (older registrations) |
 * | `fcm.googleapis.com` | also used by Opera, Brave, and other Chromium forks |
 *
 * Edge (Chromium) uses `fcm.googleapis.com` — the same FCM endpoint as
 * Chrome — since it switched to the Blink engine (Edge v79+). The older
 * EdgeHTML endpoint (`*.notify.windows.com`) is no longer issued for new
 * subscriptions and is deliberately excluded; if a subscription from an
 * EdgeHTML device surfaces, it will be rejected as `unknownProvider` so
 * that stale or attacker-injected endpoints are not silently accepted.
 *
 * **Adding a new host** requires an explicit code change and a corresponding
 * doc update here — the hardcoded list is intentional friction against
 * supply-chain attacks.
 *
 * Pure-logic only — no Service Worker or pushManager calls.
 */

/** Union result returned by {@link validateEndpoint}. */
export type EndpointValidationResult =
  | { readonly ok: true; readonly host: string }
  | {
      readonly ok: false;
      readonly reason: "notHttps" | "unknownProvider" | "malformed";
    };

/**
 * Hardcoded set of allowed push endpoint hostnames.
 *
 * Rationale: see module-level doc comment — prevents data exfiltration to
 * attacker-controlled endpoints if the server is compromised and supplies a
 * malicious push endpoint URL.
 */
const ALLOWED_PUSH_HOSTS: ReadonlySet<string> = new Set([
  "fcm.googleapis.com",
  "updates.push.services.mozilla.com",
  "push.services.mozilla.com",
  "web.push.apple.com",
]);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Validate a Web Push subscription endpoint URL.
 *
 * Validation steps, in order:
 * 1. Parse the URL — reject with `malformed` if `new URL()` throws.
 * 2. Require `https:` scheme — reject with `notHttps` otherwise.
 * 3. Check the hostname against {@link ALLOWED_PUSH_HOSTS} —
 *    reject with `unknownProvider` if the host is not in the list.
 *
 * On success returns `{ ok: true, host }` where `host` is the normalised
 * lower-case hostname.
 */
export function validateEndpoint(url: string): EndpointValidationResult {
  // Step 1: parse.
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return { ok: false, reason: "malformed" };
  }

  // Step 2: HTTPS-only.
  if (parsed.protocol !== "https:") {
    return { ok: false, reason: "notHttps" };
  }

  // Step 3: allow-list check.
  const host = parsed.hostname.toLowerCase();
  if (!ALLOWED_PUSH_HOSTS.has(host)) {
    return { ok: false, reason: "unknownProvider" };
  }

  return { ok: true, host };
}

/**
 * Expose the allow-list for documentation / diagnostics purposes.
 * Callers MUST NOT mutate the returned set.
 */
export function allowedPushHosts(): ReadonlySet<string> {
  return ALLOWED_PUSH_HOSTS;
}
